import { useCallback, useEffect, useRef, useState } from "react";
import { trackEvent, trackMixpanelEvent } from "../../utils/Analytics";
import { auth } from "../../configs/firebaseConfig";
import {
  recordLettuceTalkCardShare,
  shareLettuceTalkCard,
} from "../../utils/empathyAI/shareLettuceTalkCard";
import {
  DEFAULT_SHARE_INTRO_ID,
  getShareIntroQuoteById,
} from "./components/shareIntroOptions";

const PRIVATE_SHARE_OPENING = getShareIntroQuoteById(DEFAULT_SHARE_INTRO_ID);

function isMobileShareDevice() {
  if (typeof navigator === "undefined") return false;

  return (
    /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function openSmsComposer(text) {
  const encodedText = encodeURIComponent(text);
  const isIOS =
    /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const bodySeparator = isIOS ? "&" : "?";

  window.location.href = `sms:${bodySeparator}body=${encodedText}`;
}

function buildShareText(message = "", cardUrl) {
  const trimmedMessage = message.trim() || PRIVATE_SHARE_OPENING;

  return `${trimmedMessage}\n\n${cardUrl}`;
}

function supportsNativeShare() {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function"
  );
}

function getCleanString(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

const SHARE_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

function getValidShareId(value) {
  const shareId = getCleanString(value);
  return SHARE_ID_PATTERN.test(shareId) ? shareId : "";
}

function getShareUserState() {
  return auth.currentUser?.isAnonymous === true ? "anonymous" : "signed_in";
}

function getPreparedShareCacheKey(cardId, referredByShareId) {
  return `${cardId}::${referredByShareId || ""}`;
}

function usePreparedCardShare({
  missingCardMessage =
    "We couldn't find the generated card to share. Please create the cards again and try once more.",
  missingQuestionMessage = "Add a question before sharing this card.",
  referredByShareId = "",
  source = "Result",
} = {}) {
  const [sharing, setSharing] = useState(false);
  const [sharePreview, setSharePreview] = useState(null);
  const [shareError, setShareError] = useState("");
  const [preparedSharesByCacheKey, setPreparedSharesByCacheKey] = useState({});
  const actionInFlightRef = useRef(false);
  const validReferredByShareId = getValidShareId(referredByShareId);
  const referredByShareIdRef = useRef(validReferredByShareId);
  referredByShareIdRef.current = validReferredByShareId;

  useEffect(() => {
    setPreparedSharesByCacheKey({});
    setSharePreview(null);
    setShareError("");
  }, [validReferredByShareId]);

  const resetShareState = useCallback(({ clearPreparedUrls = false } = {}) => {
    setSharePreview(null);
    setShareError("");

    if (clearPreparedUrls) {
      setPreparedSharesByCacheKey({});
    }
  }, []);

  const prepareShare = async ({ cardId, question } = {}) => {
    if (sharing) return;

    const cleanQuestion = getCleanString(question);

    if (!cleanQuestion) {
      setShareError(missingQuestionMessage);
      return;
    }

    const cleanCardId = getCleanString(cardId);

    if (!cleanCardId) {
      setShareError(missingCardMessage);
      trackEvent("CTA", "Share card failed", `${source} missing persisted card`);
      return;
    }

    const hasNativeShare = supportsNativeShare();
    const channel = hasNativeShare ? "native" : "clipboard";
    const cacheKey = getPreparedShareCacheKey(
      cleanCardId,
      validReferredByShareId
    );
    const existingPreparedShare = preparedSharesByCacheKey[cacheKey] || null;
    const currentSelectedIntroId =
      typeof sharePreview?.selectedIntroId === "string" &&
      sharePreview.selectedIntroId
        ? sharePreview.selectedIntroId
        : DEFAULT_SHARE_INTRO_ID;
    const openSharePreview = (nextPreview = {}) => {
      setSharePreview({
        cardId: cleanCardId,
        selectedIntroId: currentSelectedIntroId,
        prefersNativeShare: hasNativeShare,
        ...nextPreview,
      });
    };

    setSharing(true);
    setShareError("");
    trackEvent("CTA", "Share card started", source);

    if (existingPreparedShare) {
      openSharePreview({
        ...existingPreparedShare,
        isPreparing: false,
        preparationFailed: false,
      });
      trackEvent(
        "CTA",
        hasNativeShare ? "Share sheet ready" : "Share preview opened",
        cleanCardId
      );
      setSharing(false);
      return;
    }

    openSharePreview({
      isPreparing: true,
      preparationFailed: false,
    });

    try {
      const preparationRequest = { cardId: cleanCardId, channel };

      if (validReferredByShareId) {
        preparationRequest.referredByShareId = validReferredByShareId;
      }

      const preparedShare = await shareLettuceTalkCard(preparationRequest);
      const cleanShareId = getCleanString(preparedShare?.shareId);
      const cleanSharePath = getCleanString(preparedShare?.sharePath);

      if (!cleanShareId || !cleanSharePath) {
        throw new Error("Share preparation response is incomplete");
      }

      const shareUrl = new URL(cleanSharePath, window.location.origin);

      if (shareUrl.origin !== window.location.origin) {
        throw new Error("Share preparation returned an unsupported URL");
      }

      const nextPreparedShare = {
        cardUrl: shareUrl.toString(),
        shareId: cleanShareId,
      };

      if (referredByShareIdRef.current !== validReferredByShareId) {
        return;
      }

      setPreparedSharesByCacheKey((currentShares) => ({
        ...currentShares,
        [cacheKey]: nextPreparedShare,
      }));

      setSharePreview((currentPreview) => ({
        ...(currentPreview || {
          cardId: cleanCardId,
          selectedIntroId: currentSelectedIntroId,
          prefersNativeShare: hasNativeShare,
        }),
        ...nextPreparedShare,
        isPreparing: false,
        preparationFailed: false,
      }));
      trackEvent(
        "CTA",
        hasNativeShare ? "Share sheet ready" : "Share preview opened",
        cleanCardId
      );
    } catch (error) {
      if (error.name === "AbortError") {
        trackEvent("CTA", "Share card canceled", cleanCardId);
        return;
      }

      console.error("Unable to share card", error);
      setSharePreview((currentPreview) =>
        currentPreview
          ? {
              ...currentPreview,
              cardUrl: "",
              isPreparing: false,
              preparationFailed: true,
            }
          : {
              cardId: cleanCardId,
              selectedIntroId: currentSelectedIntroId,
              prefersNativeShare: hasNativeShare,
              cardUrl: "",
              isPreparing: false,
              preparationFailed: true,
            }
      );
      setShareError("We couldn’t prepare the link.");
      trackEvent("CTA", "Share card failed", source);
    } finally {
      setSharing(false);
    }
  };

  const updateSharePreviewMessage = (nextSelectedIntroId) => {
    setSharePreview((currentPreview) =>
      currentPreview
        ? {
            ...currentPreview,
            selectedIntroId: nextSelectedIntroId,
          }
        : currentPreview
    );
  };

  const closeSharePreview = () => {
    resetShareState();
  };

  const getSharePreviewText = () => {
    if (!sharePreview) return "";

    return buildShareText(
      getShareIntroQuoteById(sharePreview.selectedIntroId),
      sharePreview.cardUrl
    );
  };

  const consumePreparedShare = (cardId, shareId) => {
    const cacheKey = getPreparedShareCacheKey(
      cardId,
      validReferredByShareId
    );
    setPreparedSharesByCacheKey((currentShares) => {
      if (currentShares[cacheKey]?.shareId !== shareId) {
        return currentShares;
      }

      const nextShares = { ...currentShares };
      delete nextShares[cacheKey];
      return nextShares;
    });
    setSharePreview((currentPreview) =>
      currentPreview?.shareId === shareId ? null : currentPreview
    );
  };

  const recordFinalShareAction = async (channel) => {
    const currentPreview = sharePreview;

    if (!currentPreview?.cardId || !currentPreview?.shareId) {
      throw new Error("Prepared share is unavailable");
    }

    const recordResult = await recordLettuceTalkCardShare({
      cardId: currentPreview.cardId,
      shareId: currentPreview.shareId,
      channel,
    });

    if (recordResult?.recorded !== true) {
      throw new Error("Share action was not recorded");
    }

    consumePreparedShare(currentPreview.cardId, currentPreview.shareId);
    return currentPreview;
  };

  const openShareSheet = async () => {
    if (
      !sharePreview ||
      sharePreview.isPreparing ||
      actionInFlightRef.current ||
      !sharePreview.cardUrl
    ) {
      return;
    }

    const shareText = getSharePreviewText();
    const channel = supportsNativeShare()
      ? "native"
      : isMobileShareDevice()
        ? "sms"
        : navigator.clipboard
          ? "clipboard"
          : "";

    if (!channel) {
      setShareError("Couldn't open sharing. Please copy the link manually.");
      return;
    }

    actionInFlightRef.current = true;
    setSharing(true);
    setShareError("");

    try {
      const recordedPreview = await recordFinalShareAction(channel);

      if (channel === "native") {
        const sharePromise = navigator.share({ text: shareText });
        trackMixpanelEvent("Share Sheet Opened", {
          card_id: recordedPreview.cardId,
          share_id: recordedPreview.shareId,
          share_method: "native_share",
          user_state: getShareUserState(),
        });
        await sharePromise;
      } else if (channel === "sms") {
        openSmsComposer(shareText);
      } else {
        await navigator.clipboard.writeText(shareText);
        alert("Copied to clipboard.");
      }

      trackEvent("CTA", "Share card completed", recordedPreview.cardId);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Unable to open share sheet", error);
        setShareError(
          "We couldn’t complete sharing. Please try again in a moment."
        );
        trackEvent("CTA", "Share card failed", source);
      }
    } finally {
      actionInFlightRef.current = false;
      setSharing(false);
    }
  };

  const copyShareLink = async () => {
    if (
      !sharePreview ||
      sharePreview.isPreparing ||
      !sharePreview.cardUrl ||
      actionInFlightRef.current
    ) {
      return;
    }

    actionInFlightRef.current = true;
    setSharing(true);
    setShareError("");

    try {
      const recordedPreview = await recordFinalShareAction("clipboard");
      await navigator.clipboard.writeText(recordedPreview.cardUrl);
      alert("Link copied.");
      trackMixpanelEvent("Share Link Copied", {
        card_id: recordedPreview.cardId,
        share_id: recordedPreview.shareId,
        share_method: "copy_link",
        user_state: getShareUserState(),
      });
      trackEvent("CTA", "Copied shared card link", recordedPreview.cardId);
    } catch (error) {
      console.error("Unable to copy share link", error);
      setShareError("We couldn’t copy the link. Please try again in a moment.");
      trackEvent("CTA", "Share card failed", source);
    } finally {
      actionInFlightRef.current = false;
      setSharing(false);
    }
  };

  return {
    clearShareError: () => setShareError(""),
    closeSharePreview,
    copyShareLink,
    openShareSheet,
    prepareShare,
    resetShareState,
    shareError,
    sharePreview,
    sharing,
    updateSharePreviewMessage,
  };
}

export default usePreparedCardShare;
